import type { IconType } from "~/components/ui/icons/icon";

interface Brand {
  pattern: RegExp;
  name: string;
  icon: IconType;
}

const knownBrands: Brand[] = [
  // Custom icons (project-specific)
  { pattern: /checkfront\.com/, name: "Checkfront", icon: "checkfront" },
  { pattern: /endorphine\.ca/, name: "Endorphine", icon: "endorphine" },
  { pattern: /trailforks\.com/, name: "Trailforks", icon: "trailforks" },
  { pattern: /naver\.me|map\.naver\.com/, name: "Naver Map", icon: "naver-map" },
  // Maps
  {
    pattern: /(google\.com\/maps|maps\.google\.com|maps\.app\.goo\.gl|goo\.gl\/maps)/,
    name: "Google Maps",
    icon: "google-maps",
  },
  { pattern: /map\.kakao\.com|kko\.to/, name: "Kakao Map", icon: "kakao-map" },
  { pattern: /waze\.com/, name: "Waze", icon: "waze" },
  // Booking & Reviews
  { pattern: /airbnb\.(com|ca|ch|de|fr|co\.\w+)/, name: "Airbnb", icon: "airbnb" },
  { pattern: /booking\.com/, name: "Booking.com", icon: "booking-com" },
  { pattern: /expedia\.(com|ca|co\.\w+)/, name: "Expedia", icon: "expedia" },
  { pattern: /tripadvisor\.(com|ca|co\.\w+)/, name: "Tripadvisor", icon: "tripadvisor" },
  { pattern: /yelp\.(com|ca)/, name: "Yelp", icon: "yelp" },
  { pattern: /foursquare\.com/, name: "Foursquare", icon: "foursquare" },
  { pattern: /zomato\.com/, name: "Zomato", icon: "zomato" },
  // Outdoor & Trails
  { pattern: /alltrails\.com/, name: "AllTrails", icon: "alltrails" },
  { pattern: /komoot\.(com|de)/, name: "Komoot", icon: "komoot" },
  { pattern: /strava\.com/, name: "Strava", icon: "strava" },
  // Social
  { pattern: /instagram\.com/, name: "Instagram", icon: "instagram" },
  { pattern: /facebook\.com/, name: "Facebook", icon: "facebook" },
  { pattern: /linkedin\.com/, name: "LinkedIn", icon: "linkedin" },
  { pattern: /twitter\.com|x\.com/, name: "X", icon: "x" },
  { pattern: /threads\.net/, name: "Threads", icon: "threads" },
  { pattern: /youtube\.com|youtu\.be/, name: "YouTube", icon: "youtube" },
  { pattern: /tiktok\.com/, name: "TikTok", icon: "tiktok" },
  { pattern: /pinterest\.(com|ca|co\.\w+)/, name: "Pinterest", icon: "pinterest" },
  { pattern: /wa\.me|whatsapp\.com/, name: "WhatsApp", icon: "whatsapp" },
];

/** Detect a known brand from a URL. */
export function detectBrand(url: string): Brand | null {
  return knownBrands.find((brand) => brand.pattern.test(url)) ?? null;
}

/** Get the appropriate icon for a link. */
export function getLinkIcon(url: string): IconType {
  const brand = detectBrand(url);
  return brand?.icon ?? "external-link";
}

/** Get a human-friendly label for a link. */
export function getLinkLabel(url: string): string {
  const brand = detectBrand(url);

  if (brand) {
    const customLabel = extractBrandLabel(brand.name, url);
    if (customLabel) return customLabel;
    return brand.name;
  }

  // Unknown: show domain
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.length > 30 ? `${url.slice(0, 30)}…` : url;
  }
}

/** Extract a meaningful label from URL for specific brands. */
function extractBrandLabel(brandName: string, url: string): string | null {
  try {
    const { pathname } = new URL(url);
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length === 0) return null;

    switch (brandName) {
      // @username platforms
      case "Instagram":
      case "X":
      case "Threads":
      case "Pinterest": {
        const username = parts[0];
        if (isReservedPath(username)) return null;
        return `@${username}`;
      }

      // TikTok: URLs already have @ in path
      case "TikTok": {
        const username = parts[0];
        if (username.startsWith("@")) return username;
        if (isReservedPath(username)) return null;
        return `@${username}`;
      }

      // YouTube: @handle or /c/channel or /channel/ID
      case "YouTube": {
        const first = parts[0];
        if (first.startsWith("@")) return first;
        if (first === "c" && parts[1]) return parts[1];
        if (first === "channel" && parts[1]) return "YouTube";
        if (isReservedPath(first)) return null;
        return first;
      }

      // Facebook: /people/Name/ID, /pages/Name/ID, /groups/name, or /username
      case "Facebook": {
        const first = parts[0];
        // Structured URLs: extract the name part
        if ((first === "people" || first === "pages") && parts[1]) {
          return decodeURIComponent(parts[1]).replace(/-/g, " ");
        }
        if (first === "groups" && parts[1]) return parts[1];
        // Direct username
        if (isReservedPath(first) || first === "profile.php") return null;
        return first;
      }

      // Strava: /athletes/ID or /clubs/name
      case "Strava": {
        if (parts[0] === "clubs" && parts[1]) return parts[1];
        return null;
      }

      default:
        return null;
    }
  } catch {
    return null;
  }
}

const RESERVED_PATHS = new Set([
  "about",
  "explore",
  "feed",
  "help",
  "login",
  "logout",
  "p",
  "pin",
  "privacy",
  "reel",
  "reels",
  "search",
  "settings",
  "stories",
  "terms",
  "today",
  "watch",
]);

function isReservedPath(path: string): boolean {
  return RESERVED_PATHS.has(path.toLowerCase());
}
